/**
 * Admin Support Inbox — your in-app inbox for everything that lands at
 * support@bidorah.com (via the public /support form now, and via
 * Cloudflare Email Routing → webhook once that's enabled).
 *
 * Three tabs: Open / Replied / Closed. Each ticket shows the full
 * message, customer email (one-click mailto: reply), context dump
 * (which page they filed from, user agent), and a "private admin
 * note" textarea — internal context that never leaves the dashboard.
 */
import React, { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowsClockwiseIcon,
  EnvelopeSimpleIcon,
  LifebuoyIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  ChatTeardropDotsIcon,
} from "@phosphor-icons/react";

const TABS = [
  { key: "open", label: "Open", icon: LifebuoyIcon, accent: "rose" },
  { key: "replied", label: "Replied", icon: ChatTeardropDotsIcon, accent: "amber" },
  { key: "closed", label: "Closed", icon: ArchiveBoxIcon, accent: "zinc" },
];

function StatusPill({ status }) {
  const map = {
    open: "bg-rose-500/10 text-rose-700 border-rose-300",
    replied: "bg-amber-500/10 text-amber-800 border-amber-300",
    closed: "bg-zinc-500/10 text-zinc-700 border-zinc-300",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${map[status] || map.open}`}>
      {status}
    </span>
  );
}

function TicketCard({ ticket, onPatch }) {
  const [note, setNote] = useState(ticket.admin_note || "");
  const [savingStatus, setSavingStatus] = useState(null);

  const setStatus = async (status) => {
    setSavingStatus(status);
    try {
      await onPatch(ticket.ticket_id, { status });
    } finally {
      setSavingStatus(null);
    }
  };

  const saveNote = async () => {
    if (note === (ticket.admin_note || "")) return;
    await onPatch(ticket.ticket_id, { admin_note: note });
  };

  const replyMailto = (() => {
    const subj = encodeURIComponent(`Re: ${ticket.subject}`);
    const body = encodeURIComponent(
      `Hi ${ticket.name},\n\n\n\n—\nBidorah Support\nhttps://bidorah.com\n\n` +
        `------ Original message ------\n${ticket.message}`,
    );
    return `mailto:${ticket.email}?subject=${subj}&body=${body}`;
  })();

  return (
    <article
      className="rounded-2xl border border-zinc-200 bg-white p-5"
      data-testid="support-ticket"
      data-ticket-id={ticket.ticket_id}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={ticket.status} />
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">
              {ticket.ticket_id}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">
              {ticket.source === "email" ? "via email" : "via web"}
            </span>
            <span className="text-[10px] text-zinc-400">
              {new Date(ticket.created_at).toLocaleString()}
            </span>
          </div>
          <h3 className="font-display font-black text-base sm:text-lg tracking-tight mt-2">
            {ticket.subject}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            <strong className="text-zinc-700">{ticket.name}</strong> ·{" "}
            <a
              href={`mailto:${ticket.email}`}
              className="text-[#1D78AB] hover:underline"
            >
              {ticket.email}
            </a>
            {ticket.user_id && (
              <span className="ml-2 text-zinc-400">signed-in user: {ticket.user_id}</span>
            )}
          </p>
        </div>
        <a
          href={replyMailto}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1D78AB] hover:bg-[#1864A8] text-white text-xs font-bold px-3.5 py-1.5"
          data-testid="support-reply"
        >
          <EnvelopeSimpleIcon size={13} weight="fill" />
          Reply
        </a>
      </div>

      <pre className="mt-4 whitespace-pre-wrap break-words text-sm text-zinc-800 bg-zinc-50 border border-zinc-100 rounded-xl p-3 font-sans">
        {ticket.message}
      </pre>

      {ticket.screenshot_url && (
        <a
          href={ticket.screenshot_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block text-xs text-[#1D78AB] hover:underline"
        >
          View attached screenshot →
        </a>
      )}

      <details className="mt-3 text-xs text-zinc-500">
        <summary className="cursor-pointer hover:text-zinc-800">Context</summary>
        <pre className="mt-2 bg-zinc-50 border border-zinc-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
          {JSON.stringify(ticket.context || {}, null, 2)}
        </pre>
        {ticket.client_ip && (
          <p className="mt-1 font-mono">client_ip: {ticket.client_ip}</p>
        )}
      </details>

      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5 block">
          Private admin note
        </label>
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
          placeholder="Internal context — never shown to the customer."
          className="text-sm"
          data-testid="support-note"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {ticket.status !== "open" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatus("open")}
            disabled={!!savingStatus}
            data-testid="support-mark-open"
          >
            Re-open
          </Button>
        )}
        {ticket.status !== "replied" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatus("replied")}
            disabled={!!savingStatus}
            data-testid="support-mark-replied"
            className="gap-1.5"
          >
            <ChatTeardropDotsIcon size={14} />
            Mark replied
          </Button>
        )}
        {ticket.status !== "closed" && (
          <Button
            size="sm"
            onClick={() => setStatus("closed")}
            disabled={!!savingStatus}
            data-testid="support-mark-closed"
            className="gap-1.5"
          >
            <CheckCircleIcon size={14} weight="fill" />
            Close ticket
          </Button>
        )}
      </div>
    </article>
  );
}

export default function AdminSupport() {
  const [tab, setTab] = useState("open");
  const [data, setData] = useState({ items: [], counts: { open: 0, replied: 0, closed: 0 } });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async (which = tab) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.get("/admin/support/tickets", { params: { status: which } });
      setData(r.data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  }, [tab]);

  useEffect(() => {
    refresh(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const patchTicket = async (ticketId, body) => {
    try {
      await api.patch(`/admin/support/tickets/${ticketId}`, body);
      toast.success("Saved");
      refresh(tab);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-support">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-[#1D78AB]/10 border border-[#1D78AB]/30 grid place-items-center">
            <LifebuoyIcon size={22} weight="fill" className="text-[#1D78AB]" />
          </div>
          <div>
            <h1 className="font-display font-black text-2xl tracking-tight">Support inbox</h1>
            <p className="text-sm text-zinc-500">
              Tickets from the public /support form land here.
            </p>
          </div>
          <div className="ml-auto">
            <Button
              variant="outline"
              onClick={() => refresh(tab)}
              disabled={busy}
              className="gap-2"
              data-testid="support-refresh"
            >
              <ArrowsClockwiseIcon size={16} className={busy ? "animate-spin" : ""} />
              {busy ? "Loading…" : "Refresh"}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-zinc-200">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.key === tab;
            const n = data.counts?.[t.key] ?? 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`relative inline-flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors ${active ? "text-[#1D78AB]" : "text-zinc-500 hover:text-zinc-800"}`}
                data-testid={`support-tab-${t.key}`}
              >
                <Icon size={15} weight={active ? "fill" : "regular"} />
                {t.label}
                <span className="text-[10px] tabular-nums rounded-full bg-zinc-100 text-zinc-600 px-1.5 py-0.5">
                  {n}
                </span>
                {active && (
                  <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#1D78AB] rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {err && (
          <div className="mt-6 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {err}
          </div>
        )}

        <div className="mt-5 space-y-4">
          {data.items.length === 0 && !busy && !err && (
            <div className="rounded-2xl border-2 border-dashed border-zinc-200 px-6 py-12 text-center">
              <p className="text-sm text-zinc-500">
                No {tab} tickets right now.
              </p>
            </div>
          )}
          {data.items.map((ticket) => (
            <TicketCard
              key={ticket.ticket_id}
              ticket={ticket}
              onPatch={patchTicket}
            />
          ))}
        </div>
      </div>
      <Footer />
    </>
  );
}
